/*!
 * spy-guard.js - kicks competitor spy / store-tracer browser extensions off the site.
 *
 * Platform agnostic: works on Shopify, WooCommerce and Whop. Drop it in the <head>
 * (or anywhere) of any store. No dependencies.
 *
 * Configure by setting window.SPY_GUARD before this file loads:
 *   window.SPY_GUARD = { dest: 'https://example.com', mode: 'redirect' };
 *
 *   dest  - where a detected spy is sent. REQUIRED for redirect mode.
 *   mode  - 'monitor' (default: detect and report, never redirect)
 *           'redirect' (detect, then replace the page)
 *   debug - true to log every evaluation to the console.
 *
 * Safety rule that drives the whole design: a false positive redirects a PAYING
 * customer off a store that is spending ad money. So a redirect requires a NAMED
 * vendor signature. A bare chrome-extension:// asset is never enough on its own,
 * because password managers, ad blockers and Grammarly all inject those too.
 */
(function () {
  'use strict';

  var CFG = window.SPY_GUARD || {};
  var DEST = CFG.dest || '';
  var MODE = CFG.mode === 'redirect' ? 'redirect' : 'monitor';
  var DEBUG = !!CFG.debug;

  /* Known spy extensions, by Chrome Web Store id. Ids verified from the store
     listings; the name regex is the durable half, since ids change on republish. */
  var VENDORS = [
    { id: 'lppbajkahdbbadheilijoeegnfndhlab', name: 'PPSPY' },
    { id: 'cncdmciajmaojninbopinpijdodjgfpd', name: 'TrendTrack' },
    { id: 'hjbfbllnfhppnhjdhhbmjabikmkfekgf', name: 'Koala Inspector' },
    { id: 'kajbojdeijchbhbodifhaigbnbodjahj', name: 'SimplyTrends' },
    { id: 'nnplkdokacnobmlbeolphcbloomcodpe', name: 'Store Detector' },
    { id: 'ffdildeckkmjekfboehfjlknafaohlfb', name: 'WooCommerce Scraper' },
    { id: 'inblpolgchbcgmocpnalobjmmaldkjap', name: 'AutoDS' }
  ];

  /* Vendor fingerprints found in injected element ids, class names, custom tag
     names and shadow DOM markup. This is the layer that survives id changes. */
  var SIG = new RegExp([
    'ppspy', 'trendtrack', 'trend-track',
    'koala[-_]?inspector', 'koalainspector', 'koala[-_]?apps',
    'simplytrends', 'store[-_]?detector', 'shopify[-_]?spy',
    'shophunter', 'winninghunter', 'ecomhunt', 'alihunter',
    'pipiads', 'adspy', 'minea', 'udroppy', 'autods',
    'dropship[-_]?(helper|spy|rabbit|scraper)',
    'woocommerce[-_]?scraper', 'commerce[-_]?inspector', 'sellertools'
  ].join('|'), 'i');

  var EXT_URL = /^(chrome|moz)-extension:\/\//i;

  var tripped = false;
  var baseline = null;

  function log() {
    if (DEBUG && window.console && console.debug) {
      console.debug.apply(console, ['[spy-guard]'].concat([].slice.call(arguments)));
    }
  }

  /* ---------------------------------------------------------------- signals */

  /* Collect the strings an injected element exposes about itself. Deliberately
     does NOT read page text, so store copy can never match a vendor name. */
  function surface(el) {
    if (!el || el.nodeType !== 1) return '';
    var parts = [el.tagName || '', el.id || ''];
    var cn = el.className;
    parts.push(typeof cn === 'string' ? cn : (cn && cn.baseVal) || '');
    ['src', 'href', 'data-extension', 'data-app', 'name'].forEach(function (a) {
      try { var v = el.getAttribute(a); if (v) parts.push(v); } catch (e) {}
    });
    try { if (el.shadowRoot) parts.push(el.shadowRoot.innerHTML.slice(0, 4000)); } catch (e) {}
    return parts.join(' ');
  }

  /* An extension asset anywhere under this node. Corroborating only. */
  function hasExtAsset(el) {
    if (!el || el.nodeType !== 1) return false;
    var roots = [el];
    try { if (el.shadowRoot) roots.push(el.shadowRoot); } catch (e) {}
    for (var i = 0; i < roots.length; i++) {
      var hit = roots[i].querySelector && roots[i].querySelector('[src^="chrome-extension://"],[href^="chrome-extension://"],[src^="moz-extension://"],[href^="moz-extension://"]');
      if (hit) return true;
      var s = el.getAttribute && (el.getAttribute('src') || el.getAttribute('href'));
      if (s && EXT_URL.test(s)) return true;
    }
    return false;
  }

  function inspect(el, where) {
    if (tripped || !el || el.nodeType !== 1) return;
    var text = surface(el);
    var m = text.match(SIG);
    if (m) return trip('dom:' + m[0], where, el);
    /* Named vendor missing, but an extension asset is present: record it so the
       console shows what was seen, without acting on it. */
    if (DEBUG && hasExtAsset(el)) log('extension asset, no vendor name, ignoring:', el);
  }

  /* ----------------------------------------------------------------- action */

  function trip(reason, where, el) {
    if (tripped) return;
    tripped = true;
    window.__spyGuard = { tripped: true, reason: reason, where: where || '', mode: MODE, at: new Date().toISOString() };
    log('TRIPPED', reason, where, el);

    if (MODE !== 'redirect') return;
    if (!DEST) { log('redirect mode with no dest configured, staying put'); return; }
    try { document.documentElement.style.display = 'none'; } catch (e) {}
    try { location.replace(DEST); } catch (e) { location.href = DEST; }
  }

  /* --------------------------------------------------------------- scanning */

  function scanAll(where) {
    var nodes = document.querySelectorAll('html > *, body > *');
    for (var i = 0; i < nodes.length && !tripped; i++) {
      if (baseline && baseline.has(nodes[i])) continue;
      inspect(nodes[i], where);
    }
  }

  function watch() {
    if (!window.MutationObserver) return;
    var mo = new MutationObserver(function (records) {
      for (var i = 0; i < records.length && !tripped; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length && !tripped; j++) inspect(added[j], 'mutation');
      }
      if (tripped) mo.disconnect();
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  /* Probe the known ids. Many MV3 extensions randomise their resource origin, so
     a miss proves nothing; a hit is conclusive. */
  function probeIds() {
    if (!window.fetch) return;
    VENDORS.forEach(function (v) {
      var url = 'chrome-extension://' + v.id + '/manifest.json';
      fetch(url, { method: 'GET', mode: 'cors' })
        .then(function (r) { if (r && (r.ok || r.type === 'opaque')) trip('id:' + v.name, 'probe'); })
        .catch(function () {});
    });
  }

  /* ------------------------------------------------------------------- boot */

  function start() {
    /* Everything present once the document has parsed is the store's own markup.
       Only things injected after that are candidates. */
    baseline = new Set(document.querySelectorAll('html > *, body > *'));
    watch();
    probeIds();
    /* Extensions often paint late, and some re-inject after navigation. */
    [0, 400, 1200, 3000, 6000].forEach(function (t) { setTimeout(function () { scanAll('poll'); }, t); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.spyGuardScan = function () { tripped = false; scanAll('manual'); return window.__spyGuard || { tripped: false }; };
})();
